// Regras do envio da ENTRADA do acordo de parcelamento que valem dos dois lados:
// no navegador (src/lib/entradaAcordo.ts emite o boleto quando o termo sai) e na
// varredura (efetivar-parcelamentos manda o boleto quando o termo é assinado).
//
// Pedido do Vinícius em 16/10/2026: o boleto da entrada só vai para o cliente
// DEPOIS que ele assina o termo. Até lá o boleto fica emitido e guardado
// (`aguarda_assinatura`), e a régua de cobrança também não fala dele.
//
// Sem I/O: roda sob `node --test` (src/lib/__tests__/entradaAcordoRegras.test.ts).

import { pagadorDoAcordo } from './pagadorAcordo.ts';

/** Uma etapa do envio da entrada (emissão, WhatsApp ou e-mail). */
export interface EtapaEnvio {
  ok: boolean;
  erro: string | null;
  em: string;
  /** Telefone ou e-mail para onde foi. */
  destino?: string | null;
  /** Nosso número do boleto (só na emissão). */
  boleto?: string | null;
}

/** `parcelamentos.entrada_envio`: a última tentativa, etapa por etapa. */
export interface EntradaEnvio {
  em: string;
  /** Preenchido quando o envio automático não se aplica a este acordo. */
  pulado?: string | null;
  /** Boleto emitido e guardado: sai para o cliente quando o termo for assinado. */
  aguarda_assinatura?: boolean;
  /** Vencimento (AAAA-MM-DD) do boleto que NÃO saiu porque venceu antes da assinatura. */
  vencida?: string | null;
  emissao?: EtapaEnvio;
  whatsapp?: EtapaEnvio;
  email?: EtapaEnvio;
}

export type CanalEnvio = 'whatsapp' | 'email';

export interface ContatoCobranca {
  papel: 'titular' | 'responsavel';
  nome: string;
  email: string;
  whatsapp: string;
}

const texto = (v: unknown) => String(v ?? '').trim();

/**
 * Para quem vai a cobrança da entrada: quem PAGA. É o responsável pela dívida
 * quando o acordo diz "boletos no nome do responsável" — a mesma regra que põe o
 * boleto no nome dele (pagadorDoAcordo) — e o titular nos outros casos.
 */
export function contatoDaCobranca(termoDados: unknown): ContatoCobranca | null {
  const d = termoDados as { devedor?: any; coobrigado?: any } | null | undefined;
  if (!d) return null;
  const doResponsavel = !!pagadorDoAcordo(termoDados) && !!d.coobrigado;
  const parte = doResponsavel ? d.coobrigado : d.devedor;
  if (!parte) return null;
  return {
    papel: doResponsavel ? 'responsavel' : 'titular',
    nome: texto(parte.nome),
    email: texto(parte.email).toLowerCase(),
    whatsapp: texto(parte.whatsapp),
  };
}

/**
 * Telefone no formato que o fluxo do WhatsApp aceita (55 + DDD + número), ou null
 * quando não dá para ser um número brasileiro: DDD + 8 ou 9 dígitos.
 *
 * "64 9981-4021" (10 dígitos) passa, porque há WhatsApp em número de 8 dígitos;
 * o que não passa é número curto ou sem DDD, que só geraria falha lá na frente.
 */
export function telefoneWhatsapp(valor: unknown): string | null {
  let d = String(valor ?? '').replace(/\D/g, '');
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) d = d.slice(2);
  if (d.length !== 10 && d.length !== 11) return null;
  return `55${d}`;
}

/** Por que o canal não pode sair, ou null quando o contato do acordo serve. */
export function motivoSemContato(contato: ContatoCobranca | null | undefined, canal: CanalEnvio): string | null {
  if (canal === 'email') return contato?.email ? null : 'O acordo não tem e-mail de quem paga.';
  if (telefoneWhatsapp(contato?.whatsapp)) return null;
  return contato?.whatsapp
    ? `O WhatsApp do acordo não é um número válido (${contato.whatsapp}).`
    : 'O acordo não tem WhatsApp de quem paga.';
}

/**
 * Dias mínimos entre hoje e o vencimento da entrada, na criação do acordo. O
 * boleto só vai para o cliente depois que ele assina, e a assinatura leva de
 * minutos a dias: com a entrada vencendo em 0 a 2 dias (quase metade dos termos
 * até 16/10/2026), o boleto chegava em cima do vencimento ou já vencido.
 */
export const PRAZO_MINIMO_ENTRADA_DIAS = 3;

const DIA_MS = 86_400_000;
const diaUtc = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  return Date.UTC(a, m - 1, d);
};
const dataBr = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');

/** Erro do vencimento da entrada na criação do acordo, ou null se dá tempo. */
export function erroVencimentoEntrada(vencimento: string | null | undefined, hoje: string): string | null {
  if (!vencimento) return 'Informe o vencimento da entrada.';
  const minimo = new Date(diaUtc(hoje) + PRAZO_MINIMO_ENTRADA_DIAS * DIA_MS).toISOString().slice(0, 10);
  if (vencimento.slice(0, 10) >= minimo) return null;
  return `A entrada precisa vencer a partir de ${dataBr(minimo)} (${PRAZO_MINIMO_ENTRADA_DIAS} dias depois de hoje): `
    + 'o boleto só vai para o cliente depois que ele assina o termo.';
}

/** AAAA-MM-DD em Brasília. `toISOString` daria o dia seguinte depois das 21h. */
export const dataEmBrasilia = (agora: Date = new Date()) =>
  agora.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

/** A entrada venceu ou vence hoje: mandar esse boleto depois da assinatura não serve. */
export function entradaVencidaParaEnviar(vencimento: unknown, hoje: string): boolean {
  const v = String(vencimento ?? '').slice(0, 10);
  return !!v && v <= hoje;
}

/** Por que o boleto não saiu quando a entrada venceu antes da assinatura. */
export function motivoEntradaVencida(vencimento: string): string {
  return `O boleto da entrada vence em ${dataBr(vencimento)} e o cliente assinou nesse dia ou depois, então ele não foi mandado. `
    + 'Cancele esse boleto, emita a entrada com data nova pela tela de Faturas e mande pelo card do acordo.';
}

/** O boleto da entrada está emitido e esperando a assinatura para sair? */
export function aguardaAssinatura(envio: EntradaEnvio | null | undefined): boolean {
  return !!envio?.aguarda_assinatura && !!envio.emissao?.ok;
}

/**
 * Canais que ainda precisam sair. Canal que já chegou não sai de novo: sem isso
 * o cliente que já recebeu pelo WhatsApp receberia outra vez.
 */
export function canaisPendentes(envio: EntradaEnvio | null | undefined): CanalEnvio[] {
  return (['whatsapp', 'email'] as const).filter((c) => !envio?.[c]?.ok);
}

/** O que guardar quando o boleto é emitido antes da assinatura: a emissão e os
 *  canais que já tinham chegado (acordo do fluxo antigo, que mandava na hora). */
export function guardarAteAssinar(em: string, emissao: EtapaEnvio, anterior: EntradaEnvio | null | undefined): EntradaEnvio {
  const guardado: EntradaEnvio = { em, emissao, aguarda_assinatura: true };
  if (anterior?.whatsapp?.ok) guardado.whatsapp = anterior.whatsapp;
  if (anterior?.email?.ok) guardado.email = anterior.email;
  return guardado;
}

/** Texto da trilha depois do envio, igual no navegador e na varredura. */
export function descreverEnvio(
  resultado: EntradaEnvio,
  nomeQuemPaga: string | null | undefined,
): { tipo: 'entrada_enviada' | 'falha'; descricao: string } {
  const { emissao, whatsapp, email } = resultado;
  const falhou = (e?: EtapaEnvio) => !!e && !e.ok;
  if (!falhou(whatsapp) && !falhou(email)) {
    return {
      tipo: 'entrada_enviada',
      descricao: `Boleto da entrada${emissao?.boleto ? ` ${emissao.boleto}` : ''} enviado por WhatsApp e e-mail para ${nomeQuemPaga || 'quem paga'}.`,
    };
  }
  const partes = [
    falhou(whatsapp) ? `WhatsApp: ${whatsapp!.erro}` : null,
    falhou(email) ? `e-mail: ${email!.erro}` : null,
  ].filter(Boolean).join(' · ');
  return { tipo: 'falha', descricao: `Boleto da entrada emitido, mas não chegou por todos os canais — ${partes}` };
}
